import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GymContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    gymName: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.phone || !formData.gymName || !formData.email) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit gym inquiry
      await axios.post(`${BACKEND_URL}/api/gym-inquiries/`, {
        ownerName: formData.name,
        phone: formData.phone,
        gymName: formData.gymName,
        email: formData.email,
        inquiryType: 'gym_partnership'
      });

      // Show thank you message
      setShowThankYou(true);
    } catch (error) {
      console.error('Gym inquiry submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowThankYou(false);
    setFormData({ name: '', phone: '', gymName: '', email: '' });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#302f2c] border-[#3f4816] text-[#dfddd6] max-w-md">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#888680] hover:text-[#e8ff47] transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {!showThankYou ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-['Bebas_Neue'] text-[#e8ff47] text-3xl">
                Partner With FIT-FRAME
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="font-['DM_Sans'] text-[#dfddd6] font-semibold">
                  Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="bg-[#0d0d0d] border-[#3f4816] text-[#dfddd6] font-['DM_Sans']"
                  placeholder="Your name"
                  required
                />
              </div>

              {/* Phone Number */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="font-['DM_Sans'] text-[#dfddd6] font-semibold">
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="bg-[#0d0d0d] border-[#3f4816] text-[#dfddd6] font-['DM_Sans']"
                  placeholder="Your phone number"
                  required
                />
              </div>

              {/* Gym Name */}
              <div className="space-y-2">
                <Label htmlFor="gymName" className="font-['DM_Sans'] text-[#dfddd6] font-semibold">
                  Gym Name *
                </Label>
                <Input
                  id="gymName"
                  name="gymName"
                  value={formData.gymName}
                  onChange={handleInputChange}
                  className="bg-[#0d0d0d] border-[#3f4816] text-[#dfddd6] font-['DM_Sans']"
                  placeholder="Your gym name"
                  required
                />
              </div>

              {/* Email ID */}
              <div className="space-y-2">
                <Label htmlFor="email" className="font-['DM_Sans'] text-[#dfddd6] font-semibold">
                  Email ID *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="bg-[#0d0d0d] border-[#3f4816] text-[#dfddd6] font-['DM_Sans']"
                  placeholder="Your email address"
                  required
                />
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#e8ff47] text-[#0d0d0d] hover:opacity-90 rounded-full py-6 font-['DM_Sans'] font-semibold uppercase"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </form>
          </>
        ) : (
          <>
            {/* Thank You Message */}
            <div className="py-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-[#e8ff47] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#0d0d0d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="font-['Bebas_Neue'] text-[#e8ff47] text-3xl mb-4">
                  SUCCESS!
                </h3>
              </div>

              <p className="font-['DM_Sans'] text-[#dfddd6] text-base leading-relaxed mb-6">
                You've just taken the first step toward building a brand your gym deserves.
                <br /><br />
                We've received your details and will reach out to you shortly.
                <br /><br />
                Get ready — your gym's transformation starts here.
              </p>

              <Button 
                onClick={handleClose}
                className="bg-[#e8ff47] text-[#0d0d0d] hover:opacity-90 rounded-full px-8 py-3 font-['DM_Sans'] font-semibold uppercase"
              >
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GymContactModal;